import React, { useState } from 'react'
import styled from 'styled-components/macro'
import estyled from '@emotion/styled'
import { getGelTokens } from '@tal-gel/theming'
import { GelParagraph, GelHeading5, GelHeading6 } from '@tal-gel/components'

import arrow from './arrow-new.svg'
import Link from './Link'
import getImage from './getImage'

const Component = styled.div`
  box-shadow: 0px 6px 12px rgba(41, 43, 50, 0.12);
  border-radius: 4px;

  max-width: 1032px;
  background-color: #fff;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;

  &:hover {
    cursor: pointer;
  }

  @media only screen and (min-width: 768px) {
    padding: 32px;
  }
`

const Titles = styled.div``

const Arrow = styled.img`
  user-select: none;
  transform: ${(props) => (props.open ? 'none' : 'rotate(180deg)')};
  transition: all 0.25s ease-in-out;
`

const Border = styled.div`
  height: 1px;
  background-color: ${() => getGelTokens().brand.brandColorGrayT10};
  margin-bottom: 16px;
`

const Content = styled.div`
  padding: 16px;
  padding-top: 0;

  @media only screen and (min-width: 768px) {
    display: flex;
    padding-top: 32px;
    padding-bottom: 32px;
    padding-left: 40px;
    padding-right: 64px;
  }
`

const Action = styled.div`
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 24px;
  background-color: ${() => getGelTokens().brand.brandColorGrayT05};
  border: 2px solid ${() => getGelTokens().brand.brandColorGrayT10};
  border-radius: 4px;

  & > p {
    margin: 0;
  }

  & > p:not(:last-child) {
    margin-bottom: 8px;
  }
`

const Text = estyled.div`
  @media only screen and (min-width: 768px) {
    order: 1;
    margin-right: ${(props) => (props.card ? '88px' : 0)};

    & > p {
      font-size: 18px;
    }
  }
`

const Additions = styled.div`
  @media only screen and (min-width: 768px) {
    order: 2;
    min-width: 340px;
  }
`

const ImageContainer = styled.div`
  display: none;
  width: 340px;
  height: 340px;
  background-color: ${() => getGelTokens().brand.brandColorGrayT05};
  border-radius: 4px;

  @media only screen and (min-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const Image = styled.img`
  width: 100%;
  border-radius: 4px;
`

const List = styled.ul`
  list-style: none;
  counter-reset: my-awesome-counter;
  padding: 0;
`

const ListItem = styled.li`
  font-family: 'Pluto Sans Condensed', sans-serif;
  font-weight: normal;
  font-size: 15px;
  line-height: 20px;
  color: ${() => getGelTokens().brand.brandColorGrayT70};
  counter-increment: my-awesome-counter;

  &:before {
    content: '';
    background: ${() => getGelTokens().brand.brandColorGrayT70};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    color: white;
    text-align: center;
    margin-right: 8px;
    margin-bottom: 1px;
  }

  &:not(:last-of-type) {
    margin-bottom: 8px;
  }

  @media only screen and (min-width: 768px) {
    font-size: 18px;
  }
`

const RiskWrapper = estyled.div`
  background-color: ${() => getGelTokens().brand.brandColorGrayT05};
  display: inline-flex;
  padding: 1px 12px;
  border-radius: 112px;
  margin-bottom: 4px;

  & > p {
    text-transform: uppercase;
    font-size: 12px;
  }

  @media only screen and (min-width: 768px) {
    & > p {
      padding: 2px 14px;
      font-size: 15px;
    }
  }
`

const Collapse = ({ data, index }) => {
  const [open, setOpen] = useState(false)

  const { title, image, card, content } = data

  const handleOpen = () => {
    setOpen(!open)
  }

  const renderItem = (title, item) => {
    const { type, body } = item

    switch (type) {
      case 'heading':
        return (
          <GelHeading6
            key={title + item.body}
            style={{
              fontSize: '12px',
              textTransform: 'uppercase',
              lineHeight: '13px',
              letterSpacing: '2px',
              color: getGelTokens().brand.brandColorGrayT40
            }}
          >
            {body}
          </GelHeading6>
        )
      case 'list':
        if (body) {
          return (
            <List key={body}>
              {body.map((item) => (
                <ListItem fade key={title + item.body + item} mb={2}>
                  {item}
                </ListItem>
              ))}
            </List>
          )
        }
        break
      case 'link':
        return (
          <Link
            key={title + item.body}
            title={item.body.label}
            url={item.body.url}
          />
        )
      default:
        // paragraph
        return (
          <GelParagraph
            key={title + item.body}
            medium
            style={{
              color: getGelTokens().brand.brandColorGrayT70,
              fontWeight: 400
            }}
          >
            {body}
          </GelParagraph>
        )
    }
  }

  return (
    <Component>
      <Header onClick={handleOpen}>
        <Titles>
          <RiskWrapper>
            <GelParagraph
              small
              style={{
                fontWeight: 700,
                color: getGelTokens().brand.brandColorGrayT50
              }}
            >
              Risk {index}
            </GelParagraph>
          </RiskWrapper>
          <GelHeading5
            marginBottom={16}
            style={{ color: getGelTokens().brand.brandColorPrimary1 }}
          >
            {title}
          </GelHeading5>
        </Titles>
        <Arrow open={open} src={arrow} alt='arrow' />
      </Header>
      {open && (
        <Content>
          <Border />
          {card && (
            <Additions>
              <GelHeading6
                style={{
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  lineHeight: '13px',
                  letterSpacing: '2px',
                  color: getGelTokens().brand.brandColorGrayT40
                }}
              >
                Take action!
              </GelHeading6>
              <Action>{card.map((item) => renderItem(title, item))}</Action>
              <ImageContainer>
                <Image src={getImage(image)} alt='No image available' />
              </ImageContainer>
            </Additions>
          )}
          <Text card={card ? 1 : 0}>
            {content.map((item) => renderItem(title, item))}
          </Text>
        </Content>
      )}
    </Component>
  )
}

export default Collapse
